import { createEffect, createSignal } from "solid-js";
import { fromActorRef } from "@fleet/state-machines/solid";
import { useQuery, type UseQueryResult } from "@tanstack/solid-query";
import { useNavigate } from "@tanstack/solid-router";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { queryClient } from "./query-client";
import { api, writeSessionToken } from "./api";
import {
  cloneSession,
  type components,
  getActiveAccount,
  handleApiResponse,
  queryKeys,
  removeAccount as removeAccountReducer,
  safeRedirectTarget,
  type Session,
  sharedApi,
  type StoredAccount,
  VISITOR_ID,
} from "@stockpilot/shared";
import { clearSessionStorage, primeSessionForAccount, sessionActor } from "./session-machine";
import {
  accountsStore,
  buildAccountFromAuthResponse,
  pickDisplayName,
  readAccountsStateSync,
  readActiveAccountSync,
} from "./accounts";
import { resolveActiveAccountView, resolveTabAccount } from "@/lib/active-account";
import { activeTabAccountId, clearTabAccountId, writeTabAccountId } from "@/lib/tab-account";
import { SESSION_STALE_TIME_MS } from "./constants";

// In-memory flag to prevent race conditions where
// clearing the React/Solid Query cache causes an immediate refetch
// before the server has invalidated the cookie.
const [isSigningOutIntent, setIsSigningOutIntent] = createSignal(false);

// Re-exporting from shared for convenience if needed, but using direct imports is preferred
export { cloneSession, safeRedirectTarget };

/**
 * Standalone REACTIVE accessor of the ACTIVE per-tab account id
 * WITHOUT the `useSession()` hook ceremony. Mirrors the primary
 * (per-tab) branch of `useSession().userId` and matches the PowerSync provider's
 * DB binding. A guest tab (or no resolvable account) resolves to `VISITOR_ID`.
 */
export function activeAccountUserId(): string {
  const tabAccountId = activeTabAccountId();
  if (tabAccountId) {
    const account = resolveTabAccount(readAccountsStateSync(), tabAccountId);
    if (account) {
      return account.kind === "guest" ? VISITOR_ID : account.userId;
    }
  }
  return VISITOR_ID;
}

/**
 * Modern Session Hook using custom Go backend client.
 * Integrated with sessionActor for offline priming.
 */
export function useSession(): Omit<
  UseQueryResult<Session | null, components["schemas"]["ErrorModel"]>,
  "data" | "isPending" | "error"
> & {
  data: Session | undefined;
  userId: () => string;
  isAuthenticated: () => boolean;
  isVisitor: () => boolean;
  isPending: boolean;
  error: components["schemas"]["ErrorModel"] | null;
} {
  const snapshot = fromActorRef(sessionActor);

  const currentSession = () => snapshot().context.session;
  const isPriming = () => snapshot().value === "priming";
  const isVisitor = () => snapshot().context.isVisitor;
  const machineError = () => snapshot().context.error;

  const shouldFetch = () => !isPriming() && !isSigningOutIntent();

  const query = useQuery<Session | null, components["schemas"]["ErrorModel"]>(() => ({
    queryKey: queryKeys.auth.session(),
    queryFn: async () => {
      const result = await api.GET("/api/auth/get-session", {
        params: { query: { jwt: true } },
      });
      return result.data ?? null;
    },
    enabled: shouldFetch(),
    staleTime: SESSION_STALE_TIME_MS,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  }));

  const querySession = (): Session | null | undefined => (query.isSuccess ? query.data : undefined);

  createEffect(() => {
    if (isSigningOutIntent()) {
      return;
    }
    if (!query.isPending) {
      const activeIsGuest = readActiveAccountSync()?.kind === "guest";
      if (activeIsGuest) {
        return;
      }
      sessionActor.send({
        type: "SERVER_REPLY",
        session: querySession() ?? null,
        error: query.error,
      });
      const data = querySession();
      if (data?.user.id) {
        void accountsStore.syncProfile({
          userId: data.user.id,
          email: data.user.email,
          displayName: pickDisplayName(data.user.name, data.user.email, data.user.id),
          avatar: data.user.image ?? undefined,
          kind: "real",
          role: data.user.role ?? undefined,
        });
      }
    }
  });

  createEffect(() => {
    if (currentSession()) {
      setIsSigningOutIntent(false);
    }
  });

  const userId = (): string => {
    const tabAccountId = activeTabAccountId();
    const account = resolveTabAccount(readAccountsStateSync(), tabAccountId);
    if (account) {
      return account.kind === "guest" ? VISITOR_ID : account.userId;
    }

    const data = querySession();
    const current = currentSession();
    const effectiveSession = current ?? data;
    return effectiveSession?.user.id ?? VISITOR_ID;
  };

  const activeView = () => {
    const tabAccountId = activeTabAccountId();
    const globalSession = currentSession() ?? querySession() ?? null;
    return resolveActiveAccountView(readAccountsStateSync(), tabAccountId, globalSession);
  };

  const resolvedData = (): Session | undefined => {
    const view = activeView();
    if (view.isGuest) {
      return undefined;
    }
    if (view.account) {
      return view.user;
    }
    const cachedFallback = isSigningOutIntent() ? undefined : querySession();
    return currentSession() ?? cachedFallback ?? undefined;
  };

  return {
    ...query,
    get data() {
      return resolvedData();
    },
    userId,
    isAuthenticated: () => Boolean(resolvedData()),
    isVisitor: () => !resolvedData(),
    get isPending() {
      return isPriming() || (query.isPending && !currentSession() && !isVisitor());
    },
    get error() {
      return query.error ?? machineError();
    },
  };
}

function persistSessionFromResponse(res: unknown): void {
  if (typeof res !== "object" || res === null) {
    return;
  }
  if (!("session" in res)) {
    return;
  }
  const { session } = res;
  if (typeof session !== "object" || session === null) {
    return;
  }
  if (!("token" in session)) {
    return;
  }
  if (typeof session.token !== "string") {
    return;
  }
  writeSessionToken(session.token);
}

export async function registerAccountFromResponse(
  res: unknown,
  fallbackToken?: string,
): Promise<StoredAccount | null> {
  const account = buildAccountFromAuthResponse(res, fallbackToken);
  if (account === null) {
    return null;
  }
  await accountsStore.upsert(account);
  return account;
}

export const auth = {
  signIn: {
    email: async (body: components["schemas"]["AuthLoginBody"]) => {
      const res = await handleApiResponse(sharedApi.signIn(api, body));
      persistSessionFromResponse(res);
      await registerAccountFromResponse(res);
      return res;
    },
  },
  signUp: {
    email: async (body: components["schemas"]["AuthRegisterBody"]) => {
      const res = await handleApiResponse(sharedApi.signUp(api, body));
      persistSessionFromResponse(res);
      await registerAccountFromResponse(res);
      return res;
    },
  },
  /**
   * Revokes the session of `activeAccountId` on the server.
   *
   * The id is forwarded as an explicit `X-Active-Account` selector (see
   * `sharedApi.signOut`): logout removes the account from the local registry
   * before this call settles, so deriving the selector from that registry can
   * send an unauthenticated revocation (401) that leaves the session alive.
   */
  signOut: async (activeAccountId?: string) => {
    const out = await sharedApi.signOut(api, activeAccountId);
    writeSessionToken(null);
    return out;
  },
  getSession: async () => handleApiResponse(sharedApi.getSession(api)),
  forgotPassword: async (body: components["schemas"]["AuthForgotPasswordBody"]) =>
    handleApiResponse(sharedApi.forgotPassword(api, body)),
  resetPassword: async (body: components["schemas"]["AuthResetPasswordBody"]) =>
    handleApiResponse(api.POST("/api/auth/reset-password", { body })),
  organization: {
    setActive: async (body: components["schemas"]["SetActiveOrganizationBody"]) =>
      handleApiResponse(api.POST("/api/org/setActiveOrganization", { body })),
  },
};

export function useExecuteSignOut() {
  const navigate = useNavigate();
  const { disconnectAndClear, destroyAccountData } = usePowerSyncContext();

  return () => {
    const signingOut = readActiveAccountSync();
    const stateAfterRemove = signingOut
      ? removeAccountReducer(readAccountsStateSync(), signingOut.userId)
      : readAccountsStateSync();
    const fallback = signingOut ? getActiveAccount(stateAfterRemove) : null;

    setIsSigningOutIntent(true);
    clearSessionStorage();
    writeSessionToken(null);
    sessionActor.send({ type: "LOGOUT" });

    void queryClient.cancelQueries();
    queryClient.clear();
    queryClient.getMutationCache().clear();

    if (signingOut?.kind === "real") {
      void accountsStore.remove(signingOut.userId);
    }

    if (fallback) {
      writeTabAccountId(fallback.userId);
      void navigate({ to: "/", replace: true });
    } else {
      clearTabAccountId();
      void navigate({ to: "/login", replace: true });
    }

    void (async () => {
      try {
        // Revoke server session for the account being signed out. Pass the
        // captured userId explicitly: `accountsStore.remove` above has already
        // dropped it from the registry, so a lazily-derived selector would be
        // null (or the fallback account) and the server would NOT delete this
        // account's session.
        await auth.signOut(signingOut?.kind === "real" ? signingOut.userId : undefined);
        await (signingOut ? destroyAccountData(signingOut.userId) : disconnectAndClear());
        if (fallback?.kind === "real") {
          setIsSigningOutIntent(false);
          primeSessionForAccount(fallback);
        }
        console.info("[Auth] Background cleanup completed.");
      } catch (err) {
        console.warn("[Auth] Background cleanup failed (expected if interrupted):", err);
      }
    })();
  };
}
